import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { clearPendingTryChat } from '../../lib/tryChatHandoff';
import { Spinner } from '../../components/design-system';

export function ConfirmDelete() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setErrorMsg('Invalid link — no token provided.');
      setStatus('error');
      return;
    }

    async function confirm() {
      try {
        const { data, error } = await supabase.functions.invoke('confirm-account-deletion', {
          body: { token },
        });

        if (error) throw error;
        if (data?.error) {
          setErrorMsg(data.error);
          setStatus('error');
          return;
        }

        clearPendingTryChat();
        await supabase.auth.signOut();
        setStatus('success');
      } catch (err: unknown) {
        setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.');
        setStatus('error');
      }
    }

    void confirm();
  }, [token]);

  return (
    <div className="min-h-dvh bg-app-bg flex items-center justify-center px-4">
      <div className="w-full max-w-sm text-center">
        {status === 'loading' && (
          <div className="flex flex-col items-center gap-4">
            <Spinner />
            <p className="text-app-muted text-sm">Deleting your account…</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-app-card border border-app-border rounded-2xl p-8">
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-green-600 dark:text-green-400">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-app-fg mb-2">Account deleted</h1>
            <p className="text-sm text-app-muted mb-6">Your account and all associated data have been permanently removed.</p>
            <Link to="/login" className="text-sm text-gold font-medium underline underline-offset-2 sk-hit">
              Back to sign in
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-app-card border border-app-border rounded-2xl p-8">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-crimson">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-app-fg mb-2">Could not delete account</h1>
            <p className="text-sm text-app-muted mb-6">{errorMsg}</p>
            <Link to="/settings" className="text-sm text-gold font-medium underline underline-offset-2 sk-hit">
              Back to settings
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConfirmDelete;
